//
// Copyright 2026 DXOS.org
//

import React from 'react';

import { Ref } from '@dxos/echo';
import { URI } from '@dxos/keys';
import * as Hooks from '@dxos/react-ui/Hooks';
import * as Typography from '@dxos/react-ui/Typography';

import { translationKey } from '#translations';
import { type CreateOptions, type FormFieldRendererProps, type RefFieldDataProps } from '#types';

import { ObjectPicker } from '../ObjectPicker.tsx';
import { presentationFor } from '../presentation.tsx';
import { findRefOption } from './find-ref-option.ts';
import { useRefCandidates } from './ref-options.ts';

export type RefFieldProps = FormFieldRendererProps & RefFieldDataProps & CreateOptions;

/**
 * A single reference picked with the next `ObjectPicker` (Combobox trigger mode): a button showing the target opens a
 * searchable list of candidates with their descriptions and, when the caller can persist one, an inline create form
 * whose row takes `createOptionLabel` and `createOptionIcon`.
 */
export const RefField = ({
  type,
  readonly,
  label,
  placeholder,
  presentation,
  getValue,
  onValueChange,
  onBlur,
  createInitialValuePath,
  createFieldMap,
  createOptionIcon,
  ...props
}: RefFieldProps) => {
  const { t } = Hooks.useTranslation(translationKey);
  const { typename, options, createSchema, createLabel, create } = useRefCandidates({ ...props, refType: type });
  const selected = findRefOption(getValue(), options);
  if (!typename) {
    return null;
  }
  if (readonly || presentationFor(presentation).isStatic) {
    return (
      <Typography.Text truncate tone={selected ? 'default' : 'muted'}>
        {selected?.label ?? t('empty-readonly-ref-field.label')}
      </Typography.Text>
    );
  }

  return (
    <ObjectPicker
      options={options}
      value={selected?.id}
      placeholder={placeholder || label || t('ref-field.placeholder')}
      createSchema={createSchema}
      createInitialValuePath={createInitialValuePath}
      createFieldMap={createFieldMap}
      createLabel={createLabel}
      createIcon={createOptionIcon}
      onCreate={
        create &&
        (async (values) => {
          const created = await create(values);
          if (created) {
            onValueChange(type, Ref.make(created));
            onBlur();
          }
        })
      }
      // A pick is the whole edit, so it commits as a select does; without the blur an auto-saving form never saves it.
      onSelect={(id) => {
        onValueChange(type, id ? Ref.fromURI(URI.make(id)) : undefined);
        onBlur();
      }}
    />
  );
};
